
import React, { useState, useEffect, useRef } from 'react';

const WalletSelector = ({ wallets, selectedWallet, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState('');
  const dropdownRef = useRef(null);

  // Format wallet address to show first 4 and last 4 characters
  const formatAddress = (address) => {
    if (!address || address.length < 10) return address;
    return `${address.substring(0, 4)}...${address.substring(address.length - 4)}`;
  };

  // Format wallet name to remove email prefix
  const formatWalletName = (name) => {
    if (!name) return 'Unnamed Wallet';
    
    // Check if the name contains an email pattern (something with @ or apostrophe s)
    if (name.includes('@') || name.includes("'s")) {
      // Extract just the "Agent X Wallet" part
      const matches = name.match(/(?:.*?'s\s)?(Agent\s+\d+\s+Wallet)/i);
      if (matches && matches[1]) {
        return matches[1];
      }
      // Fallback: remove everything before "Wallet"
      return name.replace(/^.*?(?=(Wallet|$))/, '').trim() || 'Wallet';
    }
    return name;
  };

  // Copy address to clipboard
  const copyToClipboard = (e, address) => {
    e.stopPropagation(); // Prevent dropdown from closing
    
    navigator.clipboard.writeText(address)
      .then(() => {
        setCopiedAddress(address);
        setTimeout(() => setCopiedAddress(''), 2000); // Reset after 2 seconds
      })
      .catch(err => {
        console.error('Failed to copy address: ', err);
      });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle wallet selection
  const handleSelectWallet = (wallet) => {
    onSelect(wallet);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-2 text-left bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 dark:text-gray-100 transition-colors"
        data-wallet-address={selectedWallet?.address || ''}
      >
        {selectedWallet ? (
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center">
              <span className="font-medium text-gray-900 dark:text-gray-100">{formatWalletName(selectedWallet.name)}</span>
              <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                ({formatAddress(selectedWallet.address)})
              </span>
            </div>
            <div className="flex items-center">
              <button
                onClick={(e) => copyToClipboard(e, selectedWallet.address)}
                className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-full mr-2 focus:outline-none transition-colors"
                title="Copy address"
              >
                {copiedAddress === selectedWallet.address ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                    <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                  </svg>
                )}
              </button>
              <svg 
                className={`w-5 h-5 text-gray-400 dark:text-gray-500 ${isOpen ? 'transform rotate-180' : ''} transition-transform`} 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 20 20" 
                fill="currentColor"
              >
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">Select a wallet</span>
        )}
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto transition-colors">
          {wallets && wallets.length > 0 ? (
            <ul className="py-1">
              {wallets.map((wallet) => (
                <li
                  key={wallet.id}
                  onClick={() => handleSelectWallet(wallet)}
                  className={`cursor-pointer px-4 py-2 hover:bg-purple-50 dark:hover:bg-purple-900/30 ${
                    selectedWallet && selectedWallet.id === wallet.id 
                      ? 'bg-purple-100 dark:bg-purple-900/50' 
                      : ''
                  }`}
                  data-wallet-address={wallet.address}
                >
                  <div className="flex flex-col">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {formatWalletName(wallet.name)}
                      </span>
                      <div className="flex items-center">
                        <button
                          onClick={(e) => copyToClipboard(e, wallet.address)}
                          className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-full focus:outline-none transition-colors"
                          title="Copy address"
                        >
                          {copiedAddress === wallet.address ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                              <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                            </svg>
                          )}
                        </button>
                        <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-full ml-2">
                          #{wallet.id}
                        </span>
                      </div>
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {formatAddress(wallet.address)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">No wallets available</div>
          )}
        </div>
      )}
    </div>
  );
};

export default WalletSelector;
